import React, { useState } from 'react';
import WeeklySummary from './WeeklySummary';
import { WeeklySummaryData } from '../../types/weekly';
import DailySummaryCard from './DailySummaryCard';
import { DailySummaryData } from '../DailyNews/DailySummary';
import './WeekSection.css';

export interface WeekSectionProps {
  weeklySummary: WeeklySummaryData;
  dailySummaries: DailySummaryData[];
  defaultExpanded?: boolean;
  showThemes?: boolean;
  showStats?: boolean;
  showDailySummaries?: boolean;
  onDayClick?: (dailySummary: DailySummaryData) => void;
  onWeekClick?: (weeklySummary: WeeklySummaryData) => void;
  className?: string;
}

const WeekSection: React.FC<WeekSectionProps> = ({
  weeklySummary,
  dailySummaries,
  defaultExpanded = true,
  showThemes = true,
  showStats = true,
  showDailySummaries = true,
  onDayClick,
  onWeekClick,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Sort daily summaries by date
  const sortedDailySummaries = [...dailySummaries].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const featuredDailySummaries = sortedDailySummaries.filter(ds => ds.featured);
  const regularDailySummaries = sortedDailySummaries.filter(ds => !ds.featured);

  return (
    <section className={`week-section ${className}`}>
      {/* Weekly Summary */}
      <WeeklySummary
        weeklySummary={weeklySummary}
        showThemes={showThemes}
        showStats={showStats}
        showShareButtons={true}
        onClick={onWeekClick ? () => onWeekClick(weeklySummary) : undefined}
        shareUrl={`${window.location.origin}/archives/week/${weeklySummary.id}`}
      />

      {/* Daily Summaries Section */}
      {showDailySummaries && (
        <div className="week-section__daily-summaries">
        <header className="week-section__daily-summaries-header">
          <h3 className="week-section__daily-summaries-title">
            Last Week's Daily Summaries ({dailySummaries.length})
          </h3>
          
          <button
            className="week-section__toggle"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse daily summaries' : 'Expand daily summaries'}
          >
            <svg
              className={`week-section__toggle-icon ${isExpanded ? 'week-section__toggle-icon--expanded' : ''}`}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6,9 12,15 18,9" />
            </svg>
          </button>
        </header>

        {/* Daily Summaries Content */}
        <div className={`week-section__daily-summaries-content ${isExpanded ? 'week-section__daily-summaries-content--expanded' : ''}`}>
          {/* Featured Daily Summaries */}
          {featuredDailySummaries.length > 0 && (
            <div className="week-section__featured-days">
              <h4 className="week-section__section-title">Featured Days</h4>
              <div className="week-section__daily-summaries-grid">
                {featuredDailySummaries.map((dailySummary) => (
                  <DailySummaryCard
                    key={dailySummary.id}
                    dailySummary={dailySummary}
                    showStats={showStats}
                    onClick={onDayClick ? () => onDayClick(dailySummary) : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Daily Summaries */}
          {regularDailySummaries.length > 0 && (
            <div className="week-section__regular-days">
              {featuredDailySummaries.length > 0 && (
                <h4 className="week-section__section-title">All Days</h4>
              )}
              <div className="week-section__daily-summaries-grid">
                {regularDailySummaries.map((dailySummary) => (
                  <DailySummaryCard
                    key={dailySummary.id}
                    dailySummary={dailySummary}
                    showStats={showStats}
                    onClick={onDayClick ? () => onDayClick(dailySummary) : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {dailySummaries.length === 0 && (
            <div className="week-section__empty-state">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p>No daily summaries available for this week.</p>
            </div>
          )}
        </div>
      </div>
      )}
    </section>
  );
};

export default WeekSection;
